import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getComponentWiseAvgScore,
  getComponentById,
  getAsmtById,
} from "../../services/authService";
import { decodeId } from "../../utills/idUtils";

const TopStrengthAndWeaknesses = ({ isGeneratingPdf, assessment_type }) => {
  const { encodedId } = useParams();
  const id = decodeId(encodedId);
  const [strengths, setStrengths] = useState([]);

  const [weaknesses, setWeaknesses] = useState([]);
  const [asmtType, setAsmtType] = useState("");

  // Fetch component-wise average scores and then fetch descriptions

  const fetchApiData = async (id) => {
    try {
      // Fetch assessment data to determine the type (self or 360)
      const asmtData = await getAsmtById(id);
      setAsmtType(asmtData.assessment_type);
      const is360 = asmtData.assessment_type === "360";

      // Fetch component-wise average scores
      const response = await getComponentWiseAvgScore(id);
      const scores = is360
        ? response.data.filter(
          (score) => score.association_type === "participant"
        )
        : response.data.filter(
          (score) => score.association_type === "initiator"
        );

      // Get unique component IDs to avoid redundant API calls
      const uniqueComponentIds = [
        ...new Set(scores.map((score) => score.component_id)),
      ];
      const componentDetailsMap = {};
      await Promise.all(
        uniqueComponentIds.map(async (compId) => {
          const details = await getComponentById(compId);
          componentDetailsMap[compId] = details; // Map component_id to its details
        })
      );

      console.log("scores", scores);

      // Process scores with component details
      const processedScores = scores.map((score) => {
        const componentDetails = componentDetailsMap[score.component_id] || {};
        return {
          ...score,
          component_name: componentDetails.component_name || "Unknown",
          description:
            componentDetails.component_description ||
            "No description available",
          avgScore: parseFloat(score["final_score"]) || 0,
        };
      });

      // Sort scores by avgScore
      const sortedScores = processedScores.sort(
        (a, b) => b.avgScore - a.avgScore
      );

      // Define thresholds for strengths and weaknesses
      const strengthThreshold = 12.8; // Adjusted for avgScore calculation
      const weaknessThreshold = 9.6; // Adjusted for avgScore calculation

      let threshold = 3.5;
      // Determine top strengths and weaknesses
      const topStrengths = sortedScores
        .filter((score) => score.avgScore >= threshold)
        .slice(0, 2); // Take the first two items

      const bottomWeaknesses = sortedScores
        .filter((score) => score.avgScore < threshold)
        .slice(-2); // Take the last two items

      // Map strengths and weaknesses with additional details
      const strengthsWithDescription = topStrengths.map((strength) => ({
        component_name: strength.component_name,
        avgScore: strength.avgScore,
        component_description:
          strength.description || "No description available",
      }));

      const weaknessesWithDescription = bottomWeaknesses.map((weakness) => ({
        component_name: weakness.component_name,
        avgScore: weakness.avgScore,
        component_description:
          weakness.description || "No description available",
      }));
      const groupByKey = (arr, getCategoryFn) => {
        return arr.reduce((acc, comp) => {
          const categorized = getCategoryFn(comp);
          if (categorized) {
            const { key } = categorized;
            if (!acc[key]) acc[key] = [];
            acc[key].push(categorized);
          }
          return acc;
        }, {});
      };

      const getStrengthCategory = (comp) => {
        if (comp.avgScore >= 3.5 && comp.avgScore < 4) {
          return { key: "moderate", ...comp };
        } else if (comp.avgScore >= 4 && comp.avgScore < 4.5) {
          return { key: "strength", ...comp };
        } else if (comp.avgScore >= 4.5) {
          return { key: "exceptional", ...comp };
        }
        return null;
      };

      const getWeaknessCategory = (comp) => {
        if (comp.avgScore >= 3.0 && comp.avgScore < 3.5) {
          return { key: "need_development", ...comp };
        } else if (comp.avgScore < 3) {
          return { key: "critical_development", ...comp };
        }
        return null;
      };

      const groupedStrengths = groupByKey(
        strengthsWithDescription,
        getStrengthCategory
      );
      const groupedWeaknesses = groupByKey(
        weaknessesWithDescription,
        getWeaknessCategory
      );

      // Set strengths and weaknesses based on assessment type
      console.log(
        "strengthwithdescription",
        groupedStrengths,
        groupedWeaknesses
      );
      setStrengths(groupedStrengths);
      setWeaknesses(groupedWeaknesses);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  console.log("weakness", weaknesses);
  // Trigger the function on component mount or when `id` changes
  useEffect(() => {
    if (id) {
      fetchApiData(id);
    }
  }, [id]);

  return (
    <div className="lg:px-16 mt-5 p-3">
      {/* <div style={{ pageBreakAfter: "always" , breakAfter: "page" }}></div> */}
      {/* <div style={{ pageBreakAfter: "always" }}></div> */}

      <div
        className="flex flex-wrap bg-[#F0F0F0] rounded-lg"
        style={{ background: "rgb(230 230 230  / var(--tw-bg-opacity, 1))" }}
      >
        <div style={{ pageBreakBefore: "always", breakBefore: "page" }}></div>
        <div className="pt-4 rounded-tl-md rounded-tr-md w-full">
          {/* Section Title */}
          <div
            className="bg-blue-700 text-white text-lg font-bold p-2 "
            style={{ paddingBottom: "5px", fontFamily: "Helvetica" }}
          >
            SECTION 4 : Top Strengths and Areas for Improvement
          </div>

          <div className="font-[Helvetica] text-[14px] px-4">
            <h3 className="text-center text-lg font-[Helvetica] font-bold text-white bg-blue-400 py-2 mb-4">
              Interpretation of Scores
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Strengths Column */}
              <div className="space-y-4 card m-2 p-3">
                <h4 className="text-lg font-bold text-center bg-blue-400 text-white">Top Strengths</h4>

                {/* Card 1 */}
                <div className="border p-4 rounded bg-gray-300 text-center">
                  <strong>Exceptional Strength</strong>
                  <p>Average Score: ≥ 4.5</p>
                  <p>Consistently demonstrated at a high level.</p>
                </div>

                {/* Card 2 */}
                <div className="border p-4 rounded bg-gray-300 text-center">
                  <strong>Strength</strong>
                  <p>Average Score: ≥ 4.0 and &lt; 4.5</p>
                  <p>Demonstrated well and reliably.</p>
                </div>

                {/* Card 3 */}
                <div className="border p-4 rounded bg-gray-300 text-center">
                  <strong>Moderate Competency</strong>
                  <p>Average Score: ≥ 3.5 and &lt; 4.0</p>
                  <p>Demonstrated with some inconsistencies.</p>
                </div>
              </div>

              {/* Areas of Development Column */}
              <div className="space-y-4 card m-2 p-3">
                <h4 className="text-lg font-bold text-center bg-blue-400 text-white">Areas of Development</h4>

                {/* Card 1 */}
                <div className="border p-4 rounded bg-gray-300 text-center">
                  <strong>Needs Development</strong>
                  <p>Average Score: ≥ 3.0 and &lt; 3.5</p>
                  <p>Improvement required in this area.</p>
                </div>

                {/* Card 2 */}
                <div className="border p-4 rounded bg-gray-300 text-center">
                  <strong>Critical Development Area</strong>
                  <p>Average Score: &lt; 3.0</p>
                  <p>Significant focus needed; priority area.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div style={{ pageBreakBefore: "always", breakBefore: "page" }}></div>
          {/* Top Strengths Section */}
          {strengths && Object.entries(strengths).length > 0 ? (
            <>
              <div className="bg-blue-200 text-black text-md font-bold p-2 mt-5">
                Top Strength:
              </div>
              {/* <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4"> */}
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {["exceptional", "strength", "moderate"].map((key) => {

                  const items = strengths[key];
                  if (!items || items.length === 0) return null;

                  const labels = {
                    exceptional: "Exceptional Strength",
                    strength: "Strength",
                    moderate: "Moderate Competency",
                  };

                  const descriptions = {
                    exceptional:
                      "Demonstrates outstanding capability in this area, consistently exceeding expectations and serving as a role model.",
                    strength:
                      "Exhibits strong proficiency, performing effectively with minimal areas for improvement.",
                    moderate:
                      "Possesses a solid foundation in this area with room for further refinement and growth.",
                  };


                  // Sort items by avgScore descending
                  const sortedItems = [...items].sort(
                    (a, b) => b.avgScore - a.avgScore
                  );

                  // Filter top 2 highest scores (random if tie)
                  const topScore = sortedItems[0]?.avgScore;
                  const topItems = sortedItems.filter(
                    (item) => item.avgScore === topScore
                  );

                  const selectedItems =
                    topItems.length > 2
                      ? topItems.sort(() => 0.5 - Math.random()).slice(0, 2)
                      : sortedItems.slice(0, 2);


                  return (
                    <div key={key} className="mb-6 col-span-2">
                      <p className="text-lg font-semibold">{labels[key]}</p>
                      <p className="mb-2 text-sm text-gray-700">
                        {descriptions[key]}
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* {items.map((strength, index) => ( */}
                        {selectedItems.map((strength, index) => (
                          <div
                            key={index}
                            className="bg-gray-300 p-4 text-center rounded-md mb-3"
                          >
                            <div className="font-bold ">
                              {strength.component_name}
                            </div>
                            <div>{strength.component_description}</div>
                            <div className="font-bold mt-2">
                              Score: {(strength?.avgScore).toFixed(2)}
                              <span className="text-gray-500 ml-1">/ 5</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) :
            <>
              <div className="bg-blue-200 text-black text-md font-bold p-2 mt-5">
                Top Strength:
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-1 gap-4">
                <p className="mt-4 text-gray-700 text-sm">
                  No top strengths have been identified in the current assessment. This offers an opportunity
                  to reflect and develop key areas of strength.
                </p>
              </div>
            </>
          }

          {/* Areas for Improvement Section */}
          {weaknesses && Object.keys(weaknesses).length > 0 ? (
            <>
              {/* {assessment_type === "self" && ( */}
              {/* <div style={{ pageBreakBefore: "always", breakBefore: "page" }}></div> */}
              {/* )} */}

              <div className="bg-blue-200 text-black text-md font-bold p-2">
                Areas For Improvement:
              </div>
              <div className="text-left mt-5 ml-2">
                {["need_development", "critical_development"].map((key) => {
                  const items = weaknesses[key];
                  if (!items || items.length === 0) return null;

                  console.log(items, "itemss")

                  return (
                    <div key={key} className="mb-6 ">
                      <p className="text-lg font-semibold">
                        {key === "need_development"
                          ? "Needs Development"
                          : "Critical Development Area"}
                      </p>
                      <p className="mb-2 text-sm text-gray-700">
                        {key === "need_development"
                          ? "Shows some capability but requires targeted improvement to enhance effectiveness."
                          : "Significant improvement needed to meet expected standards, requiring focused effort and support."}
                      </p>

                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                        {items.map((weakness, index) => (
                          <div
                            key={index}
                            className={`p-4 rounded-md mb-3 
                              bg-gray-300`}
                          // style={{ backgroundColor: "#fed7aa" }}
                          >
                            <div className="font-bold">
                              {weakness.component_name}
                            </div>
                            <div>{weakness.component_description}</div>
                            <div className="font-bold mt-2">
                              Average Score: {(weakness?.avgScore).toFixed(2)}
                              <span className="text-gray-500 ml-1">/ 5</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* <p>Exceptional Strength:Demonstrates outstanding capability in this area, consistently exceeding
                            expectations and serving as a role model.</p>
                            <p>Strength:Exhibits strong proficiency, performing effectively with minimal areas for
                            improvement.</p>
                            <p>Moderate Competency: Possesses a solid foundation in this area with room for further refinement
                            and growth.</p> */}
              </div>
            </>
          ) :
            <>
              <div className="bg-blue-200 text-black text-md font-bold p-2">
                Areas For Improvement:
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-1 gap-4">
                <p className="mt-4 text-gray-700 text-sm">
                  No areas of development were highlighted in the current assessment. This provides a
                  moment to consolidate strengths and remain open to future growth opportunities.
                </p>
              </div>
            </>
          }
        </div>
      </div>
    </div>
  );
};

export default TopStrengthAndWeaknesses;